import { useState } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import { useLocalizedLink } from "@/hooks/useLocalizedLink";
import { useLanguage } from "@/hooks/useLanguage";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import logoHeader from "@/assets/logo-header.webp";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const practiceAreas = [
    {
      titleKey: "practices.immigration" as const,
      href: "/practice-areas/immigration-services",
      descKey: "practices.immigration.desc" as const,
    },
    {
      titleKey: "practices.criminal" as const,
      href: "/practice-areas/criminal-defense",
      descKey: "practices.criminal.desc" as const,
    },
    {
      titleKey: "practices.injury" as const,
      href: "/practice-areas/personal-injury",
      descKey: "practices.injury.desc" as const,
    },
    {
      titleKey: "practices.family" as const,
      href: "/practice-areas/family-law",
      descKey: "practices.family.desc" as const,
    },
    {
      titleKey: "practices.tax" as const,
      href: "/practice-areas/tax-law",
      descKey: "practices.tax.desc" as const,
    },
    {
      titleKey: "practices.probate" as const,
      href: "/practice-areas/probate-estate",
      descKey: "practices.probate.desc" as const,
    },
    {
      titleKey: "practices.realestate" as const,
      href: "/practice-areas/real-estate-law",
      descKey: "practices.realestate.desc" as const,
    },
    {
      titleKey: "practices.business" as const,
      href: "/practice-areas/business-law",
      descKey: "practices.business.desc" as const,
    },
  ] as const;

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Top bar with contact info */}
      <div className="hidden lg:block bg-primary text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2 text-sm">
            <div className="flex items-center gap-4 xl:gap-6">
              <a href="tel:8329303059" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">EN: (832) 930-3059</span>
              </a>
              <a href="tel:8323567254" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Phone className="h-4 w-4" />
                <span className="font-medium">ES: (832) 356-7254</span>
              </a>
              <a href="tel:8329876543" className="flex items-center gap-2 hover:text-accent transition-colors">
              <Phone className="h-4 w-4" />
              <span className="font-medium">VI: (832) 930-3059</span>
            </a>
            </div>
            <div className="flex items-center gap-4">
              <a href="mailto:info@danielalbertlaw.com" className="flex items-center gap-2 hover:text-accent transition-colors">
                <Mail className="h-4 w-4" />
                <span>info@danielalbertlaw.com</span>
              </a>
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center">
            <img 
              src={logoHeader} 
              alt="Daniel Albert Law Firm" 
              className="h-12 md:h-14 w-auto"
              width="423"
              height="57"
            />
          </LocalizedLink>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <LocalizedLink to="/" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.home')}
            </LocalizedLink>
            <LocalizedLink to="/about-us" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.about')}
            </LocalizedLink>
            
            {/* Practice Areas Dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-foreground hover:text-primary font-medium">
                    {t('header.practices')}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="grid gap-3 p-6 w-[600px] grid-cols-2">
                      {practiceAreas.map((area) => (
                        <LocalizedLink
                          key={area.href}
                          to={area.href}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        >
                          <div className="text-sm font-medium leading-none">{t(area.titleKey)}</div>
                          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                            {t(area.descKey)}
                          </p>
                        </LocalizedLink>
                      ))}
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <LocalizedLink to="/blog" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.blog')}
            </LocalizedLink>
            <LocalizedLink to="/tools" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.tools')}
            </LocalizedLink>
            <LocalizedLink to="/contact" className="text-foreground hover:text-primary font-medium transition-colors">
              {t('header.contact')}
            </LocalizedLink>
            <Button asChild variant="hero" size="lg">
              <LocalizedLink to="/appointment">{t('header.consultation')}</LocalizedLink>
            </Button>
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t">
          <nav className="container mx-auto px-4 py-4 space-y-4">
            <LocalizedLink
              to="/"
              className="block py-2 text-foreground hover:text-primary font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.home')}
            </LocalizedLink>
            <LocalizedLink
              to="/about-us"
              className="block py-2 text-foreground hover:text-primary font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.about')}
            </LocalizedLink>
            <div className="space-y-2">
              <div className="py-2 font-medium text-primary">{t('header.practices')}</div>
              {practiceAreas.map((area) => (
                <LocalizedLink
                  key={area.href}
                  to={area.href}
                  className="block py-2 pl-4 text-sm text-foreground hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t(area.titleKey)}
                </LocalizedLink>
              ))}
            </div>
            <LocalizedLink
              to="/blog"
              className="block py-2 text-foreground hover:text-primary font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.blog')}
            </LocalizedLink>
            <LocalizedLink
              to="/tools"
              className="block py-2 text-foreground hover:text-primary font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.tools')}
            </LocalizedLink>
            <LocalizedLink
              to="/contact"
              className="block py-2 text-foreground hover:text-primary font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('header.contact')}
            </LocalizedLink>
            <div className="pt-4 space-y-2">
              <a href="tel:8329303059" className="block">
                <Button variant="phone" className="w-full">
                  <Phone className="mr-2 h-4 w-4" />
                  Call English: (832) 930-3059
                </Button>
              </a>
              <a href="tel:8323567254" className="block">
                <Button variant="phone" className="w-full">
                  <Phone className="mr-2 h-4 w-4" />
                  Llamar Español: (832) 356-7254
                </Button>
              </a>
              <Button asChild variant="hero" className="w-full">
                <LocalizedLink to="/appointment" onClick={() => setMobileMenuOpen(false)}>
                  {t('header.consultation')}
                </LocalizedLink>
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
